/* Bio Gold — <bg-brand-slider> (unit 13, sections/bg-brand-slider.liquid).
   Arrows, dots and page maths for the brand logo carousel. Scrolling itself is native
   (CSS scroll-snap on the track); this element only reads scroll state and calls scrollTo.
   Without the data-slider attribute (grid layout) the element is inert. */
if (!customElements.get('bg-brand-slider')) {
  customElements.define(
    'bg-brand-slider',
    class BgBrandSlider extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-slider')) return;
        this.track = this.querySelector('.bg-brand-slider__track');
        if (!this.track) return;

        this.slides = Array.from(this.track.children);
        this.prev = this.querySelector('[name="previous"]');
        this.next = this.querySelector('[name="next"]');
        this.dotsEl = this.querySelector('[data-dots]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.singleStep = window.matchMedia('(min-width: 1200px)'); // Slick: scroll 1 at >= 1200, else a full page
        this.page = 0;
        this.pages = 1;

        if (!this.bound) {
          this.bound = true;
          this.prev?.addEventListener('click', () => this.goTo(this.page - 1));
          this.next?.addEventListener('click', () => this.goTo(this.page + 1));
          this.track.addEventListener(
            'scroll',
            () => {
              cancelAnimationFrame(this.raf);
              this.raf = requestAnimationFrame(() => this.update());
            },
            { passive: true }
          );
        }

        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this.track);

        if (window.Shopify?.designMode) {
          this.onBlockSelect = (event) => {
            const index = this.slides.indexOf(event.target);
            if (index > -1) this.goTo(Math.floor(index / this.step));
          };
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }

        this.layout();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.raf);
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      layout() {
        const count = this.slides.length;
        const viewport = this.track.clientWidth;
        if (viewport === 0) return; // hidden (display:none) — ResizeObserver fires again when shown

        if (count < 2) {
          this.slideWidth = viewport;
          this.perView = 1;
          this.step = 1;
          this.pages = 1;
        } else {
          this.slideWidth =
            this.slides[1].getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left;
          if (this.slideWidth <= 0) return;
          this.perView = Math.max(1, Math.round(viewport / this.slideWidth));
          this.step = this.singleStep.matches ? 1 : this.perView;
          this.pages = count <= this.perView ? 1 : Math.ceil((count - this.perView) / this.step) + 1;
        }

        if (this.dotsEl && this.dotsEl.childElementCount !== this.pages) this.renderDots();
        this.update();
      }

      renderDots() {
        const { label = 'Load slide', of = 'of', controls } = this.dotsEl.dataset;
        const dots = Array.from({ length: this.pages }, (_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'bg-brand-slider__dot';
          button.setAttribute('aria-label', `${label} ${index + 1} ${of} ${this.pages}`);
          if (controls) button.setAttribute('aria-controls', controls);
          button.addEventListener('click', () => this.goTo(index));
          return button;
        });
        this.dotsEl.replaceChildren(...dots);
        this.dotsEl.hidden = this.pages < 2;
      }

      maxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      update() {
        const max = this.maxScroll();
        const x = this.track.scrollLeft;
        const pageWidth = this.step * this.slideWidth || 1;
        this.page = x >= max - 1 ? this.pages - 1 : Math.min(this.pages - 1, Math.round(x / pageWidth));

        this.prev?.setAttribute('aria-disabled', String(x <= 0));
        this.next?.setAttribute('aria-disabled', String(x >= max - 1));
        this.dotsEl?.querySelectorAll('button').forEach((button, index) => {
          if (index === this.page) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }

      goTo(target) {
        const page = Math.max(0, Math.min(this.pages - 1, target));
        const max = this.maxScroll();
        // The last page is end-clamped (Slick never shows blank space after the final logo).
        const left = page === this.pages - 1 ? max : Math.min(page * this.step * this.slideWidth, max);
        this.track.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }
  );
}
